import { ResponseStatus } from '@meta-press/shared-plugins';

/**
 * Strategy for polling long-running async operations
 * Defines how to determine if a response needs polling and how to poll it
 */
export interface PollingStrategy<T> {
	/**
	 * Determine if the response needs polling (i.e., operation is not yet complete)
	 * @param response The response from the initial request
	 * @returns true if polling is needed, false if operation is complete
	 */
	needsPolling(response: T): boolean;

	/**
	 * Extract the response ID needed for polling from the initial response
	 * @param response The response from the initial request
	 * @returns The ID to use for polling, or undefined if not available
	 */
	extractResponseId(response: T): string | undefined;

	/**
	 * Interval between polling attempts in milliseconds
	 */
	pollInterval: number;

	/**
	 * Maximum number of polling attempts before giving up
	 */
	maxPollAttempts: number;
}

/**
 * Default polling strategy for OpenAI API responses
 * Polls responses with status Queued (3) or InProgress (0) every 6 seconds
 * Maximum 75 attempts (7.5 minutes total)
 */
export const OPENAI_POLLING_STRATEGY: PollingStrategy<any> = {
	needsPolling: (response: any): boolean => {
		return (
			response.status === ResponseStatus.Queued ||
			response.status === ResponseStatus.InProgress
		);
	},

	extractResponseId: (response: any): string | undefined => {
		return response.id;
	},

	pollInterval: 6000, // 6 seconds

	maxPollAttempts: 75, // 75 * 6 seconds = 450 seconds = 7.5 minutes
};
